import {StyleSheet} from 'aphrodite';
import {css} from 'aphrodite';
import {getNamedType, Kind} from 'query-api-graphql';
import React, {useCallback} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {TagSchema} from 'constants/RegistryConstants';
import GraphQLFieldSelectorWrapper from 'pipelines/query-api-step-config/GraphQLFieldSelectorWrapper';
import QueryApiAllSchemaSelector, {
  QueryApiSchemaSelectorSchema,
} from 'pipelines/query-api-step-config/QueryApiAllSchemaSelector';
import RelatedResultsFormList from 'pipelines/query-api-step-config/RelatedResultsFormList';
import {ENTITY_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT} from 'pipelines/query-api-step-config/SchemaDatasetCreationConstants';
import {
  DEFAULT_FIELD,
  DEFAULT_FIELD_SELECTION,
} from 'pipelines/query-api-step-config/SchemaDatasetCreationConstants';
import {
  assertHasArchiveReasonArg,
  fieldHasRegistryArg,
  getFieldDisplayNameFn,
  typeHasDefaultField,
} from 'pipelines/query-api-step-config/SchemaDatasetCreationUtils';
import QueryApiFilters, {
  buildRegistryArgument,
  DisabledQueryApiFilters,
  NOT_ARCHIVED_ARCHIVE_REASON_ARGUMENT,
} from 'query-api/QueryApiFilters';
import {fetchQueryApiTypeExtension, QueryApiIntrospection} from 'query-api/useQueryApiIntrospection';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Stepper, {StepperStep} from 'ui-library/stepper/Stepper';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';
import removeNulls from 'util/removeNulls';

import {
  getColumnMappingStepperStep,
  ColumnMappingStepperStepProps,
} from '../column-mapping/ColumnMappingStepperStep';
import {DataFrameNameState} from '../DataFrameCreationTypes';
import DataFrameNameField from '../steps/DataFrameNameField';
import {FormState} from './RegistrySchemaDataFrameCreationTypes';

interface QueryApiEntrypointOptions {
  schemaSelectorElementTitle: string;
  schemaSelectorElement: JSX.Element;
  shouldRenderRelatedResults: boolean;
}

interface RegistrySchemaStepperFormProps {
  queryApiIntrospection: QueryApiIntrospection;

  queryFormState: FormState;
  setQueryFormState: (state: FormState, newDataFrameName?: string) => void;
  setIsManuallyChanged: (isManuallyChanged: boolean) => void;

  dataFrameNameState: DataFrameNameState;
  setDataFrameNameState: (state: DataFrameNameState) => void;
  columnMappingProps?: ColumnMappingStepperStepProps;
}

function RegistrySchemaStepperForm({
  queryFormState,
  setQueryFormState,
  setIsManuallyChanged,
  dataFrameNameState,
  queryApiIntrospection,
  setDataFrameNameState,
  columnMappingProps,
}: RegistrySchemaStepperFormProps): JSX.Element {
  const queryListingField =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryField(queryFormState.entityListingFieldName);

  const handleChangeSchema = useCallback(
    (schema: QueryApiSchemaSelectorSchema | null): void => {
      setIsManuallyChanged(false);
      if (schema == null) {
        setQueryFormState({entityListingFieldName: null});
        return;
      }

      const listingField = queryApiIntrospection.getQueryListingFieldForSchema(schema.id);
      assert(listingField != null, 'User selected a schema without a corresponding Query API listing field');

      const errorLoggingData = {
        schemaId: schema.id,
        schemaName: schema.name,
        listingFieldName: listingField.name,
      };

      const hasDefaultField = typeHasDefaultField(listingField.type);
      if (APIUtil.isTagSchema(schema.id)) {
        assertAndContinue(
          hasDefaultField,
          `User selected a schema without the "${DEFAULT_FIELD}" field`,
          errorLoggingData
        );

        fetchQueryApiTypeExtension(getNamedType(listingField.type).name);
      }

      const hasArchiveReasonArg = assertHasArchiveReasonArg(listingField, errorLoggingData);

      // Entities that are from entry schemas cannot be registered, and therefore entry schemas listing fields
      // in the query api do not have a `registry` argument.
      const hasRegistryArg = fieldHasRegistryArg(listingField);

      // Pass the dataFrameName along with the form state so that the parent can set both
      // query and dataFrameName atomically in a single config update (avoids race condition
      // where separate onChangeConfig calls overwrite each other due to stale closures).
      const newDataFrameName = !dataFrameNameState.hasUserEdits ? `${schema.name} data` : undefined;

      setQueryFormState(
        {
          entityListingFieldName: listingField.name,
          entityFilterArguments: removeNulls([
            hasArchiveReasonArg ? NOT_ARCHIVED_ARCHIVE_REASON_ARGUMENT : null,
            hasRegistryArg && schema.registryId != null ? buildRegistryArgument(schema.registryId) : null,
          ]),
          entityFieldSelections: {
            kind: Kind.SELECTION_SET,
            selections: hasDefaultField ? [DEFAULT_FIELD_SELECTION] : [],
          },
          relatedResultsFormStates: [],
        },
        newDataFrameName
      );

      if (newDataFrameName != null) {
        setDataFrameNameState({...dataFrameNameState, name: newDataFrameName});
      }
    },
    [
      dataFrameNameState,
      queryApiIntrospection,
      setDataFrameNameState,
      setQueryFormState,
      setIsManuallyChanged,
    ]
  );

  const tagSchemaFilterFn = useCallback(
    (schema: TagSchema) => queryApiIntrospection.hasQueryListingFieldForSchema(schema.id),
    [queryApiIntrospection]
  );

  const schemaId =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryFieldSchemaId(queryFormState.entityListingFieldName);

  const queryApiEntrypointOptions: QueryApiEntrypointOptions =
    BenchlingConfig.ENABLE_ANALYSIS_TOOL_ALL_TYPE_QUERY_OPTION
      ? {
          schemaSelectorElementTitle: 'Schema',
          schemaSelectorElement: (
            <QueryApiAllSchemaSelector
              schemaId={schemaId}
              handleSchemaChange={handleChangeSchema}
              queryApiIntrospection={queryApiIntrospection}
            />
          ),
          shouldRenderRelatedResults: false,
        }
      : {
          schemaSelectorElementTitle: 'Entity schema',
          schemaSelectorElement: (
            <TagSchemaSelector
              // Make sure all props are memoized so that TagSchemaSelector (which can be slow to render when
              // there are a lot of schemas) does not rerender unnecessarily.
              onChange={handleChangeSchema}
              allowNone={false}
              selectedTagSchemaId={schemaId}
              tagSchemaFilterFn={tagSchemaFilterFn}
            />
          ),
          shouldRenderRelatedResults: true,
        };

  return (
    <Stepper>
      <StepperStep title="Select Registry schema">
        <FieldLabel label={queryApiEntrypointOptions.schemaSelectorElementTitle} />
        <div className={css(styles.entitySchemaSelectorWrapper)}>
          <div className={css(styles.entitySchemaSelector)}>
            {queryApiEntrypointOptions.schemaSelectorElement}
          </div>
          {queryFormState.entityListingFieldName == null || schemaId == null ? (
            <DisabledQueryApiFilters />
          ) : (
            <QueryApiFilters
              filterArguments={queryFormState.entityFilterArguments}
              onChangeFilterArguments={(newFilterArguments) => {
                setQueryFormState({
                  ...queryFormState,
                  entityFilterArguments: newFilterArguments,
                });
              }}
              schemaId={schemaId}
            />
          )}
        </div>
      </StepperStep>
      <StepperStep title="Select columns">
        {queryFormState.entityListingFieldName != null && queryListingField != null && (
          <GraphQLFieldSelectorWrapper
            field={queryListingField}
            logicalDepthLimit={ENTITY_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT}
            selectionSet={queryFormState.entityFieldSelections}
            setSelectionSet={(newSelectionSet) => {
              setQueryFormState({...queryFormState, entityFieldSelections: newSelectionSet});
            }}
            getFieldDisplayName={getFieldDisplayNameFn(queryListingField, queryApiIntrospection)}
            {...pick(
              queryApiIntrospection,
              'getTypeDisplayName',
              'graphQLSchema',
              'isDeprecatedField',
              'isBacklinkField',
              'isUserDefinedField',
              'isUserComputedField'
            )}
          />
        )}
      </StepperStep>
      {queryApiEntrypointOptions.shouldRenderRelatedResults && (
        <StepperStep title="Add Results">
          {queryFormState.entityListingFieldName == null || queryListingField == null ? null : (
            <RelatedResultsFormList
              relatedResultsFormStates={queryFormState.relatedResultsFormStates}
              onChangeRelatedResultFormStates={(newRelatedResultsFormStates) => {
                setQueryFormState({...queryFormState, relatedResultsFormStates: newRelatedResultsFormStates});
              }}
              entityListingField={queryListingField}
              queryApiIntrospection={queryApiIntrospection}
            />
          )}
        </StepperStep>
      )}
      {columnMappingProps != null && getColumnMappingStepperStep(columnMappingProps)}
      <StepperStep title="Name table">
        <DataFrameNameField
          {...dataFrameNameState}
          onNameChange={(newName: string) => {
            setDataFrameNameState({name: newName, hasUserEdits: true});
          }}
        />
      </StepperStep>
    </Stepper>
  );
}

// Memoized since unnecessary rerenders can cause elements such as the Filters popover to unexpectedly close.
export default React.memo(RegistrySchemaStepperForm);

const styles = StyleSheet.create({
  entitySchemaSelectorWrapper: {
    display: 'flex',
  },
  entitySchemaSelector: {
    flexGrow: 1,
    marginRight: 6,
  },
});
